import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { X, Lightbulb, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface Tooltip {
  id: string;
  target: string;
  title: string;
  description: string;
  position: 'top' | 'bottom' | 'left' | 'right';
}

const tooltips: Tooltip[] = [
  {
    id: 'ai-search',
    target: '[data-tutorial="ai-search"]',
    title: 'Smart Search',
    description: 'Describe what you\'re looking for in plain language. Try "funny movies about time travel" or "emotional dramas from the 90s"',
    position: 'bottom'
  },
  {
    id: 'watchlist',
    target: '[data-tutorial="watchlist"]',
    title: 'Personal Watchlist',
    description: 'Save movies you want to watch later. Your watchlist syncs across all devices.',
    position: 'left'
  },
  {
    id: 'profile-button',
    target: '[data-tutorial="profile-button"]',
    title: 'Your Profile',
    description: 'Track your watch history, ratings, and achievements. See your movie stats and customize your experience.',
    position: 'bottom'
  }
];

export const FeatureTooltips = () => {
  const [activeTooltip, setActiveTooltip] = useState<Tooltip | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dismissed, setDismissed] = useState<string[]>([]);

  useEffect(() => {
    // Load dismissed tooltips from localStorage
    const saved = localStorage.getItem('dismissed-tooltips');
    if (saved) {
      setDismissed(JSON.parse(saved));
    }

    // Show first tooltip after 2 seconds if not dismissed
    const timer = setTimeout(() => {
      showNextTooltip();
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  const showNextTooltip = () => {
    const undismissedTooltips = tooltips.filter(t => !dismissed.includes(t.id));
    if (undismissedTooltips.length === 0) return;

    const nextTooltip = undismissedTooltips[currentIndex % undismissedTooltips.length];
    const element = document.querySelector(nextTooltip.target);
    
    if (element) {
      setActiveTooltip(nextTooltip);
    }
  };

  const handleDismiss = (tooltipId: string) => {
    const newDismissed = [...dismissed, tooltipId];
    setDismissed(newDismissed);
    localStorage.setItem('dismissed-tooltips', JSON.stringify(newDismissed));
    setActiveTooltip(null);

    // Show next tooltip after a delay
    setTimeout(() => {
      setCurrentIndex(prev => prev + 1);
      showNextTooltip();
    }, 500);
  };

  const handleNext = () => {
    if (activeTooltip) {
      handleDismiss(activeTooltip.id);
    }
  };

  if (!activeTooltip) return null;

  const element = document.querySelector(activeTooltip.target);
  if (!element) return null;

  const rect = element.getBoundingClientRect();
  const getPosition = () => {
    switch (activeTooltip.position) {
      case 'bottom':
        return { top: rect.bottom + 10, left: rect.left + rect.width / 2 };
      case 'top':
        return { top: rect.top - 10, left: rect.left + rect.width / 2 };
      case 'left':
        return { top: rect.top + rect.height / 2, left: rect.left - 10 };
      case 'right':
        return { top: rect.top + rect.height / 2, left: rect.right + 10 };
    }
  };

  const position = getPosition();

  return (
    <AnimatePresence>
      {activeTooltip && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50"
            onClick={() => handleDismiss(activeTooltip.id)}
          />

          {/* Highlight */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="fixed z-50 pointer-events-none"
            style={{
              top: rect.top - 4,
              left: rect.left - 4,
              width: rect.width + 8,
              height: rect.height + 8,
              border: '2px solid hsl(var(--primary))',
              borderRadius: '8px',
              boxShadow: '0 0 20px hsl(var(--primary) / 0.5)'
            }}
          />

          {/* Tooltip */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="fixed z-50"
            style={{
              top: position.top,
              left: position.left,
              transform: 'translate(-50%, 0)'
            }}
          >
            <Card className="w-80 shadow-2xl border-primary/20">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Lightbulb className="h-5 w-5 text-primary" />
                    <h4 className="font-semibold">{activeTooltip.title}</h4>
                  </div>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6"
                    onClick={() => handleDismiss(activeTooltip.id)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">{activeTooltip.description}</p>
                <Button
                  size="sm"
                  onClick={handleNext}
                  className="w-full"
                >
                  Got it! <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};